import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { LucideIcon } from 'lucide-react';

interface RoomCountSelectProps {
  icon: LucideIcon;
  label: string;
  fieldName: string;
  defaultValue: string;
  readOnly?: boolean;
}

const RoomCountSelect = ({
  icon: Icon,
  label,
  fieldName,
  defaultValue,
  readOnly = false
}: RoomCountSelectProps) => {
  const { setValue, watch } = useFormContext();
  const value = watch(fieldName, defaultValue);
  const [displayValue, setDisplayValue] = React.useState<string>(String(value || ''));

  // Sync display value when form value changes externally
  React.useEffect(() => {
    setDisplayValue(String(value || ''));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty string while user is typing
    setDisplayValue(inputValue);

    // If empty, set form value to 0 but keep display empty
    if (inputValue === '') {
      setValue(fieldName, 0);
      return;
    }

    // Parse as number and validate
    const numValue = parseInt(inputValue, 10);

    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue >= 0) {
      setValue(fieldName, numValue);
    }
  };

  const handleBlur = () => {
    // On blur, if empty, show 0
    if (displayValue === '') {
      setDisplayValue('0');
      setValue(fieldName, 0);
    }
  };

  return (
    <div className="form-group">
      <Label className="form-label flex items-center gap-2">
        <Icon size={16} />
        {label}
      </Label>
      <Input
        type="number"
        min="0"
        step="1"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={readOnly}
        className={`form-select ${readOnly ? "bg-gray-100" : ""}`}
        placeholder="0"
      />
    </div>
  );
};

export default RoomCountSelect;
